import { useGlobalState } from './useGlobalState';
import {
  fetchAuthorizedSchools,
  fetchAuthorizedBranches,
  type BranchOption,
} from '@/auth/userAccess';
import { rememberSchoolList } from '@/api/schoolScope';
import { useServerList } from './useServerList';
import type { ListParams } from '@/api/pagedList';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import * as api from '@/api/school';
import type { SchoolContactInput, SchoolInput, SchoolRecord } from '@/models';
import { useMutationWithToast } from './useMutationWithToast';

// --- Schools ---------------------------------------------------------------

/** The organization's schools. For the school the app works in, use `useCurrentSchool`. */
/** The school list rarely changes (saving a school invalidates it), so tab changes reuse it. */
export const authorizedSchoolsKey = (organizationId: number, userId: number) =>
  [...queryKeys.school.list, 'authorized', organizationId, userId] as const;

export const authorizedBranchesKey = (organizationId: number, userId: number) =>
  [...queryKeys.school.list, 'branches', organizationId, userId] as const;

/** Fabrica branches remain selectable before their school profile has been configured. */
export const useBranches = () => {
  const { state } = useGlobalState();
  const access = state.access;
  return useQuery({
    queryKey: authorizedBranchesKey(
      access?.activeOrganization.organizationId ?? 0,
      access?.user.userId ?? 0,
    ),
    enabled: Boolean(access && state.token),
    queryFn: () =>
      fetchAuthorizedBranches(
        state.token!,
        access!.activeOrganization.organizationId,
        access!.branchId,
      ),
    staleTime: 60_000,
  });
};

export const branchOptionForSchool = (school: SchoolRecord): BranchOption => ({
  organizationId: school.organizationId,
  branchId: school.branchId,
  branchName: school.schoolName,
  school,
});

export const useSchools = () => {
  const { state } = useGlobalState();
  const access = state.access;
  return useQuery({
    queryKey: access
      ? authorizedSchoolsKey(access.activeOrganization.organizationId, access.user.userId)
      : queryKeys.school.list,
    queryFn: async () => {
      const schools =
        access && state.token
          ? await fetchAuthorizedSchools(
              state.token,
              access.activeOrganization.organizationId,
              access.branchId,
            )
          : await api.listSchools();
      rememberSchoolList(schools);
      return schools;
    },
    staleTime: 60_000,
  });
};

/** Creates a school, or updates it when `schoolId` is given. */
export const useSaveSchool = () =>
  useMutationWithToast({
    mutationFn: (vars: { schoolId?: number; input: SchoolInput }) =>
      vars.schoolId ? api.updateSchool(vars.schoolId, vars.input) : api.createSchool(vars.input),
    invalidate: [queryKeys.school.list],
    successMessage: 'School saved.',
  });

// --- School contacts -------------------------------------------------------

export const useSchoolContacts = (schoolId: number | null) =>
  useQuery({
    queryKey: queryKeys.school.contacts(schoolId),
    // Only runs once a school is known (see `enabled`).
    queryFn: () => api.listSchoolContacts(schoolId as number),
    enabled: schoolId !== null,
  });

/** Creates a contact, or updates it when `schoolContactId` is given. */
export const useSaveSchoolContact = () =>
  useMutationWithToast({
    mutationFn: (vars: {
      schoolId: number;
      schoolContactId?: number;
      input: SchoolContactInput;
    }) =>
      vars.schoolContactId
        ? api.updateSchoolContact(vars.schoolId, vars.schoolContactId, vars.input)
        : api.createSchoolContact(vars.schoolId, vars.input),
    invalidate: [queryKeys.school.allContacts],
    successMessage: 'Contact saved.',
  });

export const useDeleteSchoolContact = () =>
  useMutationWithToast({
    mutationFn: (vars: { schoolId: number; schoolContactId: number }) =>
      api.deleteSchoolContact(vars.schoolId, vars.schoolContactId),
    invalidate: [queryKeys.school.allContacts],
    successMessage: 'Contact deleted.',
  });

export const useSchoolContactsTable = (params: ListParams = {}, schoolId?: number) =>
  useServerList(api.listSchoolContactsPage, queryKeys.school.allContacts, params, { schoolId });
